import { useRef, useState } from "react";
import { Calendar as CalIcon, Clock as ClockIcon, X } from "lucide-react";

/**
 * <HVDateInput />
 *
 * Theme-aware date / time / datetime picker that renders DD/MM/YYYY in
 * the visible field while persisting ISO (YYYY-MM-DD or full ISO) to
 * the parent. Replaces native `<input type=date>` whose calendar icon
 * is invisible on dark mode and forces locale-dependent formats.
 *
 * The actual picker UI uses a hidden native input (browsers ship a
 * great picker for free) — we just hijack the display layer.
 *
 * Props:
 *   value      — ISO string ("2026-05-03" for date / "2026-05-03T08:30" for datetime / "08:30" for time)
 *   onChange   — (iso) => void
 *   mode       — "date" | "time" | "datetime"  (default "date")
 *   disabled
 *   testId
 *   placeholder
 *   className
 *
 * Locked 2026-02 #66 — DD/MM/YYYY everywhere per DESIGN_CONTRACT §8.
 *
 * [Iter 290zp.21] Time-picker wheel-bounce bug fix.
 *   Bug: on mobile (iOS Safari, Android Chrome) the native time picker
 *   is a scroll wheel. With the input fully controlled (`value={value}`),
 *   every intermediate tick of the wheel fired onChange → React re-render
 *   → input.value re-applied mid-spin → wheel "snaps" to wrong slot.
 *   User taps 06:00, sees 08:00 or 10:00 commit instead.
 *
 *   Fix: dual-tracking.
 *     • `live` is a local buffer that drives the native input. It moves
 *       with the user's wheel gesture without round-tripping through
 *       React's render cycle of the parent.
 *     • The parent's `onChange` is called only on `blur` (commit) — when
 *       the wheel comes to rest and the picker is dismissed.
 *     • Incoming `value` prop only overwrites `live` when the input is
 *       NOT focused, so external resets (e.g. "Today" button) still flow
 *       in but never interrupt an in-flight wheel scroll.
 *     • Native `change` events still fire during scroll, but only update
 *       the local buffer — the parent stays calm.
 */
export default function HVDateInput({
  value, onChange, mode = "date", disabled, testId, placeholder, className = "",
}) {
  // [Iter 290zp.22c] Time mode now uses two simple <select> elements
  // (hour + minute), identical pattern to DateTimePicker.jsx which has
  // been working reliably across all browsers in Meetings, JC, Tasks,
  // Calls, etc. for months. The native <input type="time"> + showPicker
  // approach Chrome desktop uses a custom popup whose change events
  // don't reach the React input through our pointer-events-none
  // overlay → user picks 06:00 in the popup but the field stays empty.
  // Two native <select>s commit per-click and need no special handling.
  if (mode === "time") {
    return (
      <HVTimeSelect
        value={value}
        onChange={onChange}
        disabled={disabled}
        testId={testId}
        placeholder={placeholder}
        className={className}
      />
    );
  }
  return (
    <HVDateOrDateTime
      value={value}
      onChange={onChange}
      mode={mode}
      disabled={disabled}
      testId={testId}
      placeholder={placeholder}
      className={className}
    />
  );
}

// [Iter 290zp.22c] Time-only picker — two <select>s (hour + minute),
// commits to parent on every change. Same proven pattern as the
// hour/minute selects inside DateTimePicker.jsx.
function HVTimeSelect({ value, onChange, disabled, testId, placeholder, className = "" }) {
  // value comes in as "HH:MM" (24-hour, 5 chars). Split for two selects.
  const [hh, mm] = String(value || "").split(":");
  const hour = (hh || "").padStart(2, "0");
  const minute = (mm || "").padStart(2, "0");

  const commit = (h, m) => {
    if (!h && !m) { onChange(""); return; }
    onChange(`${(h || "00").padStart(2, "0")}:${(m || "00").padStart(2, "0")}`);
  };

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

  return (
    <div className={`hv-timeselect flex items-center gap-1 ${className}`} data-testid={testId}>
      <select
        value={hour}
        onChange={(e) => commit(e.target.value, minute)}
        disabled={disabled}
        aria-label="Hour"
        className="hv-input text-sm w-20"
        data-testid={testId ? `${testId}-hour` : undefined}
      >
        <option value="">{placeholder ? "--" : "HH"}</option>
        {hours.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-[#647087] text-sm">:</span>
      <select
        value={minute}
        onChange={(e) => commit(hour, e.target.value)}
        disabled={disabled}
        aria-label="Minute"
        className="hv-input text-sm w-20"
        data-testid={testId ? `${testId}-minute` : undefined}
      >
        <option value="">MM</option>
        {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <ClockIcon className="w-3.5 h-3.5 text-[#1FB6C1] shrink-0 ml-1" />
    </div>
  );
}

// Date and Datetime branch (unchanged from pre-Iter-290zp.21 behaviour).
function HVDateOrDateTime({
  value, onChange, mode, disabled, testId, placeholder, className = "",
}) {
  const nativeRef = useRef(null);
  const [focused, setFocused] = useState(false);

  const display = formatDisplay(value, mode);

  const onNativeChange = (e) => {
    onChange(e.target.value || "");
  };

  const openPicker = () => {
    if (disabled) return;
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch { /* fallthrough */ }
    }
    el.focus();
    el.click();
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  const Icon = mode === "time" ? ClockIcon : CalIcon;
  const nativeType = mode === "time" ? "time" : (mode === "datetime" ? "datetime-local" : "date");

  return (
    <div className={`hv-dateinput relative ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        className={`hv-input text-sm w-full flex items-center justify-between gap-2 text-left ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-[#1FB6C1]/60"} ${focused ? "ring-1 ring-[#1FB6C1]/60" : ""}`}
        data-testid={testId ? `${testId}-button` : undefined}
      >
        <span className={`flex-1 truncate ${display ? "text-[var(--hv-text)]" : "text-[#647087]"}`}>
          {display || placeholder || (mode === "time" ? "HH:MM" : mode === "datetime" ? "DD/MM/YYYY HH:MM" : "DD/MM/YYYY")}
        </span>
        {value && !disabled && (
          <X
            className="w-3 h-3 text-[#647087] hover:text-rose-300"
            onClick={clear}
            data-testid={testId ? `${testId}-clear` : undefined}
          />
        )}
        <Icon className="w-3.5 h-3.5 text-[#1FB6C1] shrink-0" />
      </button>
      {/* Native input is sized to fill the wrapper but rendered with
          opacity:0 so the click anywhere on the button surface still
          triggers the browser's native picker. */}
      <input
        ref={nativeRef}
        type={nativeType}
        value={value || ""}
        onChange={onNativeChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 opacity-0 pointer-events-none"
        data-testid={testId ? `${testId}-native` : undefined}
      />
    </div>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }

/** Format an ISO value for DD/MM/YYYY display (or HH:MM for time, or DD/MM/YYYY HH:MM for datetime). */
export function formatDisplay(iso, mode = "date") {
  if (!iso) return "";
  if (mode === "time") {
    // accept "08:30" or full ISO
    if (/^\d{2}:\d{2}$/.test(iso)) return iso;
    const d = new Date(iso);
    if (Number.isFinite(d.getTime())) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return iso;
  }
  if (mode === "datetime") {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  // date — accept "2026-05-03" or full ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Convert a DD/MM/YYYY (or DD-MM-YYYY) string back to ISO YYYY-MM-DD. Returns "" if unparseable. */
export function parseDisplayDate(s) {
  if (!s) return "";
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/);
  if (!m) return "";
  const [, d, mo, y] = m;
  return `${y}-${pad(mo)}-${pad(d)}`;
}
